/**
 * Side-pane viewer for a local file the user opened from the tree. Mirrors
 * the artifact canvas mount pattern (sibling of the chat motion.div, sliding
 * in from the right) but is read-only — content is loaded once from the
 * `FileSystemFileHandle` and cached in component state.
 *
 * Per kind:
 *  - markdown → streamdown
 *  - text     → <pre>
 *  - image    → <img src="blob:...">
 *  - csv      → papaparse → <table>
 *  - html     → sandboxed <iframe>
 *  - external → "preview not available" card with download CTA
 *
 * @module file-agent/components/local-file-viewer
 */

import { motion } from "framer-motion";
import { Download, ExternalLink, Loader2, X } from "lucide-react";
import Papa from "papaparse";
import { useEffect, useMemo, useState } from "react";
import { Streamdown } from "streamdown";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getFileBadge } from "../lib/file-badge";
import { useLocalFileViewerStore, type LocalFile } from "../store/local-file-viewer-store";

/** Top-level viewer. Reads the active file from the store and dispatches by kind. */
export function LocalFileViewer() {
  const file = useLocalFileViewerStore((s) => s.file);
  const close = useLocalFileViewerStore((s) => s.close);

  if (!file) return null;

  return (
    <motion.div
      key="local-file-viewer"
      className="flex flex-[3] flex-col bg-background min-w-0 min-h-0 overflow-hidden border-l border-border/60"
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
    >
      <ViewerHeader file={file} onClose={close} />
      <div className="flex-1 min-h-0 overflow-hidden">
        <ViewerBody file={file} />
      </div>
    </motion.div>
  );
}

/** Header strip with badge, title, breadcrumb path, and action buttons. */
function ViewerHeader({ file, onClose }: { file: LocalFile; onClose: () => void }) {
  const badge = getFileBadge(file.name);
  const dirSegments = file.path.split("/").slice(0, -1);

  return (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border/60 bg-background px-4">
      <span
        className={cn(
          "inline-flex h-5 min-w-5 items-center justify-center rounded px-1.5",
          "font-mono text-[9.5px] font-semibold tracking-tight",
          badge.className,
        )}
      >
        {badge.label}
      </span>
      <span className="truncate text-sm font-medium text-foreground">{file.name}</span>
      {dirSegments.length > 0 && (
        <>
          <span className="text-xs text-muted-foreground/60">·</span>
          <span className="truncate text-xs text-muted-foreground">{dirSegments.join("/")}</span>
        </>
      )}
      <div className="flex-1" />
      <DownloadButton file={file} />
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} aria-label="Close">
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

/** Streams the file out of the FSA handle as a download. */
function DownloadButton({ file }: { file: LocalFile }) {
  const handleDownload = async () => {
    const f = await file.handle.getFile();
    const url = URL.createObjectURL(f);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7"
      onClick={handleDownload}
      aria-label="Download"
      title="Download"
    >
      <Download className="h-4 w-4" />
    </Button>
  );
}

/** Routes to the right body renderer based on the file's inferred kind. */
function ViewerBody({ file }: { file: LocalFile }) {
  switch (file.kind) {
    case "markdown":
      return <MarkdownBody file={file} />;
    case "text":
      return <TextBody file={file} />;
    case "csv":
      return <CsvBody file={file} />;
    case "image":
      return <ImageBody file={file} />;
    case "html":
      return <HtmlBody file={file} />;
    case "external":
    default:
      return <ExternalBody file={file} />;
  }
}

/**
 * Loads UTF-8 text from an FSA file handle. Returns `[content, isLoading, error]`
 * so renderers can show inline loading/error states without each one re-doing
 * the boilerplate.
 */
function useFileText(file: LocalFile) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setContent(null);
    setError(null);

    (async () => {
      try {
        const f = await file.handle.getFile();
        const text = await f.text();
        if (!cancelled) setContent(text);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [file]);

  return { content, error, isLoading: content === null && error === null };
}

function LoadingState() {
  return (
    <div className="flex h-full items-center justify-center text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-8 text-center">
      <p className="text-sm font-medium text-foreground">Could not read this file</p>
      <p className="max-w-sm text-xs text-muted-foreground">{message}</p>
    </div>
  );
}

function MarkdownBody({ file }: { file: LocalFile }) {
  const { content, error, isLoading } = useFileText(file);
  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  return (
    <div className="size-full overflow-auto px-7 py-6">
      <article className="prose prose-sm prose-zinc dark:prose-invert mx-auto max-w-3xl [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
        <Streamdown>{content ?? ""}</Streamdown>
      </article>
    </div>
  );
}

function TextBody({ file }: { file: LocalFile }) {
  const { content, error, isLoading } = useFileText(file);
  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  return (
    <pre className="size-full overflow-auto whitespace-pre-wrap break-words bg-background p-6 font-mono text-xs leading-relaxed text-foreground">
      {content}
    </pre>
  );
}

interface ParsedCsv {
  headers: string[];
  rows: string[][];
  truncated: boolean;
}

/** Cap rows we parse so a giant CSV doesn't lock up the main thread. */
const CSV_ROW_LIMIT = 500;

function CsvBody({ file }: { file: LocalFile }) {
  const { content, error, isLoading } = useFileText(file);

  const parsed = useMemo<ParsedCsv | null>(() => {
    if (!content) return null;
    const result = Papa.parse<string[]>(content, { skipEmptyLines: true });
    const all = (result.data as string[][]).filter((r) => Array.isArray(r));
    if (all.length === 0) return { headers: [], rows: [], truncated: false };
    const [headers, ...rows] = all;
    const truncated = rows.length > CSV_ROW_LIMIT;
    return {
      headers,
      rows: truncated ? rows.slice(0, CSV_ROW_LIMIT) : rows,
      truncated,
    };
  }, [content]);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!parsed) return null;

  return (
    <div className="size-full overflow-auto">
      <table className="w-full border-collapse text-xs">
        <thead className="sticky top-0 bg-muted/50 backdrop-blur">
          <tr>
            {parsed.headers.map((h, i) => (
              <th
                key={i}
                className="border-b border-border/60 px-3 py-2 text-left font-medium text-foreground"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {parsed.rows.map((row, ri) => (
            <tr key={ri} className="hover:bg-muted/40">
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className="border-b border-border/40 px-3 py-1.5 text-muted-foreground"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {parsed.truncated && (
        <div className="border-t border-border/60 bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
          Showing first {CSV_ROW_LIMIT} rows. Download to see the full file.
        </div>
      )}
    </div>
  );
}

function ImageBody({ file }: { file: LocalFile }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let revoke: string | null = null;
    setUrl(null);
    setError(null);

    (async () => {
      try {
        const f = await file.handle.getFile();
        const next = URL.createObjectURL(f);
        revoke = next;
        if (!cancelled) setUrl(next);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();

    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [file]);

  if (error) return <ErrorState message={error} />;
  if (!url) return <LoadingState />;

  return (
    <div
      className="flex size-full items-center justify-center overflow-auto p-8"
      style={{
        backgroundImage:
          "linear-gradient(45deg,rgb(228 228 231 / 0.4) 25%,transparent 25%),linear-gradient(-45deg,rgb(228 228 231 / 0.4) 25%,transparent 25%),linear-gradient(45deg,transparent 75%,rgb(228 228 231 / 0.4) 75%),linear-gradient(-45deg,transparent 75%,rgb(228 228 231 / 0.4) 75%)",
        backgroundSize: "14px 14px",
        backgroundPosition: "0 0, 0 7px, 7px -7px, -7px 0",
      }}
    >
      <img
        src={url}
        alt={file.name}
        className="max-h-full max-w-full rounded-md shadow-[0_2px_8px_rgb(0_0_0/0.10)]"
      />
    </div>
  );
}

function HtmlBody({ file }: { file: LocalFile }) {
  const { content, error, isLoading } = useFileText(file);
  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  return (
    <iframe
      title={file.name}
      sandbox=""
      srcDoc={content ?? ""}
      className="size-full border-0 bg-white"
    />
  );
}

/** Office binary fallback — preview isn't browser-renderable, offer to download. */
function ExternalBody({ file }: { file: LocalFile }) {
  const badge = getFileBadge(file.name);

  const handleDownload = async () => {
    const f = await file.handle.getFile();
    const url = URL.createObjectURL(f);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex size-full items-center justify-center bg-muted/20 p-12">
      <div className="max-w-md text-center">
        <div
          className={cn(
            "mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-xl",
            badge.className,
          )}
        >
          <span className="font-mono text-sm font-semibold tracking-tight">{badge.label}</span>
        </div>
        <h3 className="mb-2 text-base font-semibold text-foreground">
          Preview not available in browser
        </h3>
        <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
          {file.name} is in a format that renders best in its native app.
          Download a copy to open it locally.
        </p>
        <div className="flex items-center justify-center gap-2">
          <Button onClick={handleDownload} className="gap-1.5">
            <Download className="h-3.5 w-3.5" />
            Download
          </Button>
          <Button variant="outline" onClick={handleDownload} className="gap-1.5">
            <ExternalLink className="h-3.5 w-3.5" />
            Open externally
          </Button>
        </div>
      </div>
    </div>
  );
}
